import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getUserStampCards, adjustUserStampCard } from "@/lib/api/admin";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner, SpinnerCenter } from "@/components/Spinner";

type AdjustStampCardDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uid: string;
};

export function AdjustStampCardDialog({ open, onOpenChange, uid }: AdjustStampCardDialogProps) {
  const user = useAdminAuth();
  const queryClient = useQueryClient();

  const { data: cards, isLoading } = useQuery({
    queryKey: ["user-stamp-cards", uid],
    queryFn: () => getUserStampCards(user!, uid),
    enabled: open && !!user,
  });

  // stamp_card_id -> edited value (string, digit-filtered)
  const [values, setValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (cards) {
      setValues(Object.fromEntries(cards.map((c) => [c.stamp_card_id, String(c.stamps)])));
    }
  }, [cards]);

  function handleOpenChange(next: boolean) {
    if (!next) {
      setValues({});
      setFormError("");
    }
    onOpenChange(next);
  }

  async function handleSave() {
    if (!user || !cards) return;
    const dirty = cards.filter((c) => values[c.stamp_card_id] !== String(c.stamps));
    if (dirty.length === 0) return;

    setFormError("");
    setIsSaving(true);
    try {
      const outcomes = await Promise.allSettled(
        dirty.map((c) =>
          adjustUserStampCard(user, uid, c.stamp_card_id, Number(values[c.stamp_card_id] || "0"))
        )
      );
      const failed = outcomes.filter((o) => o.status === "rejected");
      void queryClient.invalidateQueries({ queryKey: ["user-stamp-cards", uid] });
      void queryClient.invalidateQueries({ queryKey: ["user", uid] });
      void queryClient.invalidateQueries({ queryKey: ["user-activity", uid] });

      const succeededCount = dirty.length - failed.length;
      if (succeededCount > 0) {
        toast.success(`${succeededCount} stamp card${succeededCount === 1 ? "" : "s"} updated`);
      }
      if (failed.length > 0) {
        toast.error(`${failed.length} stamp card${failed.length === 1 ? "" : "s"} failed to save`);
      } else {
        handleOpenChange(false);
      }
    } finally {
      setIsSaving(false);
    }
  }

  const dirtyCount = (cards ?? []).filter(
    (c) => values[c.stamp_card_id] !== String(c.stamps)
  ).length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm min-w-0">
        <DialogHeader>
          <DialogTitle>Adjust Stamp Cards</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <SpinnerCenter />
        ) : !cards || cards.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This user has no stamp card progress at any store yet.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {cards.map((c) => (
              <li
                key={c.stamp_card_id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <Label htmlFor={`stamps-${c.stamp_card_id}`} className="min-w-0 flex-1 truncate">
                  {c.store_name}
                </Label>
                <Input
                  id={`stamps-${c.stamp_card_id}`}
                  type="text"
                  inputMode="numeric"
                  className="w-20 shrink-0"
                  value={values[c.stamp_card_id] ?? ""}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [c.stamp_card_id]: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                />
              </li>
            ))}
          </ul>
        )}

        {formError && <p className="text-sm text-destructive">{formError}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={dirtyCount === 0 || isSaving}>
            {isSaving ? (
              <Spinner className="size-4 border-white" />
            ) : (
              `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
